import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { ZendeskService } from '@libs/src/zendesk/zendesk.service';

import { fadeInEnter } from '@libs/src/animations/animations';
import { Group } from '@libs/src/models/group.model';
import { TENANT_JOIN_TYPES } from '@libs/src/tenant/tenant-join-types.enum';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import type { IAppState } from '@main-client/src/app/app.state';
import { GetHubs } from '@main-client/src/app/hubs/hubs.actions';

@Component({
  animations: [fadeInEnter],
  selector: 'app-user-onboarding-view-hubs',
  templateUrl: './user-onboarding-view-hubs.component.html',
})
export class UserOnboardingViewHubsComponent implements OnInit {
  @Input() currentStepNumber: number;
  @Input() numberOfSteps: number;
  @Input() tenant: Tenant;
  @Output() navigateToNextView = new EventEmitter<Group>();
  hubsState = this.store.select('hubs');
  joinableHubs = this.hubsState.pipe(
    map((hubsState) =>
      (hubsState.items ?? []).filter(
        (item) => TENANT_JOIN_TYPES.closed !== item.join_type,
      ),
    ),
  );
  userState = this.store.select('user');
  hoverStates: Record<string, boolean> = {};
  selectedGroupId: string | undefined;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly zendesk: ZendeskService,
    public store: Store<IAppState>,
  ) {}

  ngOnInit() {
    this.selectedGroupId =
      this.route.snapshot.queryParamMap?.get('selectedGroupId');
    if (this.selectedGroupId) {
      this.navigateToNextView.emit({ _id: this.selectedGroupId });
      return;
    }
    this.joinableHubs
      .pipe(
        filter((hubs) => 1 === hubs.length),
        take(1),
        tap((hubs) =>
          setTimeout(() => {
            this.navigateToNextView.emit(hubs[0]);
          }, 0),
        ),
      )
      .subscribe();
  }

  loadMoreHubs() {
    combineLatest([this.hubsState, this.userState])
      .pipe(
        take(1),
        tap(([hubsState, user]) => {
          if (
            hubsState.isLoading ||
            !hubsState.items?.length ||
            hubsState.items.length >= hubsState.totalCount
          ) {
            return;
          }
          const excludeIds = user.groups.map(
            (membership) => membership.document._id,
          );
          const params = {
            excludeIds,
            ...hubsState.searchFilters,
            keyword: hubsState?.keyword ?? '',
            skip: hubsState.items.length,
          };
          this.store.dispatch(GetHubs(params));
        }),
      )
      .subscribe();
  }

  showZendesk() {
    this.zendesk.showZendesk();
  }
}
